// src/config/routes.ts
import { ReactNode } from 'react'
import { IconDefinition, faTableCells, faListCheck, faBug, faInfoCircle, faLayerGroup, faFileLines, faMagnifyingGlass, faTimeline, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import Home from '../components/home/Home';
import IssuesList from '../components/issues/IssueList';
import IssueDetailPage from '../components/issueDetail/IssueDetailPage';
import Settings from '../components/settings/Settings';
import SignIn from '../components/auth/SignIn';
import SignUp from '../components/auth/SignUp';
import DstackPage2 from '../components/dstack/DstackPage2';
import DlogPage from '../components/dlog/DlogPage';
import DInternalPage from '../pages/DInternalPage';
import MyProfilePage from '../pages/MyProfilePage';
import PulsePage from '../components/pulse/PulsePage';
import TeamSelectionPage from '../components/teams/TeamSelectionPage';
import CsvAnalysisPage from '../pages/CsvAnalysisPage';
import ProbePage from '../pages/ProbePage';
import MyTimeSliceComponent from '../pages/DInternals/TimeSliceView';

export interface RouteConfig {
  name: string;
  path: string;
  element: React.ComponentType;
  icon?: IconDefinition | ReactNode; // For DInternalPage, changed string to IconDefinition
  description?: string; // For DInternalPage  
  protected: boolean;
  public: boolean; // Keep public flag for clarity if needed
  showInNav?: boolean; // Optional: Flag to control header visibility
}

const baseRoutes: RouteConfig[] = [
  {
    name: 'Home',
    path: '/',
    element: Home,
    protected: false,
    public: true,
    showInNav: false, // Show Home in nav
  },
  {
    name: 'Sign In',
    path: '/signin',
    element: SignIn,
    protected: false,
    public: true,
    showInNav: false, // Don't show Sign In in nav links
  },
  {
    name: 'Sign Up',
    path: '/signup',
    element: SignUp,
    protected: false,
    public: true,
    showInNav: false, // Don't show Sign Up in nav links
  },  
  // {
  //   name: 'Dstack',
  //   path: '/dstack',
  //   element: DstackPage1,
  //   protected: false,
  //   public: true,
  //   showInNav: true,
  // },
  {
    name: 'Teams',
    path: '/teams',
    element: TeamSelectionPage,
    protected: true,  // Or true, depending on your requirements
    public: true,
    showInNav: true,
  },  
  {
    name: 'Pulse',
    path: '/pulse',
    element: PulsePage,
    protected: true,
    public: false, // Should not be public if it's a protected route
    showInNav: true,
  },
  {
    path: '/probe',
    name: 'Probe',
    element: ProbePage,
    icon: faMagnifyingGlass,
    description: 'Investigate request details and event timelines.',
    protected: true,
    public: false,  // Should not be public if it's a protected route
    showInNav: true,
  },
  {
    name: 'Settings',
    path: '/settings',
    element: Settings,
    protected: true,
    public: false,
    showInNav: false, // Settings accessed via profile menu
  }
];


// Separate list for internal tool routes to be displayed on DInternalPage
export const internalRoutes: RouteConfig[] = [
  {
    path: '/timeslice', // Example with a placeholder teamId
    name: 'Time Slice',
    element: MyTimeSliceComponent,
    icon: faTimeline,
    description: 'View Time slices.',
    protected: false,
    public: false,    
    showInNav: false,
  },
  {
    path: '/issues/:teamId', // Example with a placeholder teamId
    name: 'Issues Explorer',
    element: IssuesList,  // Assuming IssuesPage can handle a teamId
    icon: faListCheck,
    description: 'Browse and analyze issues for a specific team.',
    protected: true,
    public: false,    
    showInNav: false,
  },
  {
    path: '/issue/:issueId',
    name: 'Issue Detail Viewer',
    element: IssueDetailPage, // Assuming IssueDetailPage can handle an issueId
    icon: faBug,
    description: 'View detailed information about a specific issue.',
    protected: true,
    public: false,
    showInNav: false,
  },
  {
    path: '/analysis/csv',
    name: 'CSV Analysis',
    element: CsvAnalysisPage,
    icon: faTableCells,
    description: 'Analyze CSV data for insights.',
    protected: false,
    public: false,    
    showInNav: false,
  },  
  {
    name: 'Dstack',
    path: '/dstack2',
    element: DstackPage2,
    icon: faLayerGroup, 
    description: 'Advanced stack trace analysis.',    
    protected: true,
    public: false,
    showInNav: false, // disabled for now on 6/2/2025. enable when we have full functionality
  },  
  {
    name: 'Dlog',
    path: '/dlog',
    element: DlogPage,
    icon: faFileLines, // faFileAlt is an alias, faFileLines is common for logs
    description: 'View and analyze structured logs.',     
    protected: true,
    public: false,
    showInNav: false, // disabled for now on 6/2/2025. enable when we have full functionality
  },
  {
    name: 'My Profile',
    path: '/my/profile',
    element: MyProfilePage,
    icon: faInfoCircle,
    description: 'Show my profile and settings.',
    protected: false,
    public: false,
    showInNav: false,
  },
];

// Combine base routes with dynamically generated internal routes
export const routes: RouteConfig[] = [
  ...baseRoutes,
  // Route for the DInternalPage itself
  {
    name: 'DInternal', // Changed from DIntenral
    path: '/dinternal',
    element: DInternalPage,
    protected: false, // Adjust as needed
    public: false,
    showInNav: false, // Or false, depending on if you want a direct link in the header
  },
  // Dynamically add internal routes with the /dinternal prefix
  ...internalRoutes.map(route => ({
    ...route,
    path: `/dinternal${route.path}`, // Prepend /dinternal
    showInNav: false, // Internal routes are not shown in the main nav directly
  })),
];